import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import invalidateCache from 'utils/api/invalidateCache';
import { AccountsService } from 'services/accounts/accounts';
import { DepartmentsService } from 'services/departments/departments';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { setToast } from 'redux/modules/toast/actions';
import { useDispatch } from 'react-redux';
import { throwError } from 'rxjs';
import { translate } from 'components/Translation';
import { finalize } from 'rxjs/operators';
import styles from 'assets/jss/material-dashboard-pro-react/components/addNumberDialogStyle';
import { makeStyles } from '@material-ui/core';
import { SingleNumber } from 'utils/api/models/numbers/singleNumber';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import AddNumbersConfirmationDialog from './subcomponents/AddNumbersConfirmationDialog';
import DialogBody from './subcomponents/DialogBody';

// @ts-ignore
const useStyles = makeStyles(styles);

interface INumber extends SingleNumber {
	department?: SingleDepartment;
	selectedDepartment?: SingleDepartment;
}

interface IAddNumbersDialogProps {
	accountId: string;
	isDialogOpen: boolean;
	setIsDialogOpen: (isDialogOpen: boolean) => void;
	selectedDepartmentId: string;
	departments: Array<any>;
	numbersAdded: () => void;
	numbersLength?: number;
}

const AddNumbersDialog = ({
	isDialogOpen,
	setIsDialogOpen,
	accountId,
	departments,
	selectedDepartmentId,
	numbersAdded,
	numbersLength,
}: IAddNumbersDialogProps) => {
	const [searchInput, setSearchInput] = useState<string>('');
	const [notAssignedToDepartment, setNotAssignedToDepartment] = useState<boolean>(true);
	const [confirmationDialog, setConfirmationDialog] = useState<boolean>(false);
	const [numbers, setNumbers] = useState<INumber[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [selectedNumbers, setSelectedNumbers] = useState<INumber[]>([]);
	const [numbersToAdd, setNumbersToAdd] = useState<SingleNumber[]>([]);
	const [confirmationDialogState, setConfirmationDialogState] = useState<'departments' | 'numbers'>('departments');
	const dispatch = useDispatch();
	const classes = useStyles();
	const selectedNumbersLength = selectedNumbers.length > 0 ? ` (${selectedNumbers.length})` : '';
	const isMounted = useRef(true);

	const findDepartment = useCallback(
		(departmentId?: string) => {
			if (!departmentId) {
				return null;
			}
			return departments.find((department: SingleDepartment) => department.deptId === departmentId);
		},
		[departments]
	);

	const selectedDepartment = useMemo(() => findDepartment(selectedDepartmentId), [
		selectedDepartmentId,
		findDepartment,
	]);

	const fetchNumbers = useCallback(() => {
		setLoading(true);
		AccountsService.getNumbersForAccount(accountId)
			.pipe(finalize(() => isMounted.current && setLoading(false)))
			.subscribe(
				({ numbers }) => {
					if (isMounted.current) {
						const numbersWithDepartments: Array<INumber> = _.chain(numbers)
							.filter((number) => number.departmentId !== selectedDepartmentId)
							.map((number) => {
								const department = findDepartment(number.departmentId);
								return {
									...number,
									department,
								};
							})
							.value();
						setNumbers(numbersWithDepartments);
					}
				},
				(error) => throwError(error)
			);
		// eslint-disable-next-line
	}, [accountId, selectedDepartmentId, numbersLength, findDepartment]);

	useEffect(() => {
		fetchNumbers();
	}, [fetchNumbers]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const clearDialog = () => {
		setSearchInput('');
		setNotAssignedToDepartment(true);
	};

	const departmentsToChange = useMemo(() => {
		return selectedNumbers
			.filter(
				(selectedNumber: SingleNumber) =>
					selectedNumber.departmentId && selectedNumber.departmentId !== selectedDepartmentId
			)
			.map((selectedNumber: SingleNumber) => ({
				...selectedNumber,
				selectedDepartment,
			}));
	}, [selectedNumbers, selectedDepartmentId, selectedDepartment]);

	const filteredNumbers = useMemo(() => {
		const usersIds: Array<string> = _.chain(selectedNumbers)
			.filter((number: SingleNumber) => !!number.userId)
			.map('userId')
			.uniq()
			.value();
		return _.filter(numbers, (number) => usersIds.indexOf(number.userId) >= 0);
	}, [selectedNumbers, numbers]);

	const numbersWithUserAssigned = useMemo(() => {
		return _.filter(selectedNumbers, (number: SingleNumber) => number.userId);
	}, [selectedNumbers]) as Array<INumber>;

	const addNumbers = () => {
		setIsDialogOpen(false);
		if (departmentsToChange.length > 0) {
			setConfirmationDialogState('departments');
			setConfirmationDialog(true);
		} else if (filteredNumbers.length !== numbersWithUserAssigned.length) {
			setConfirmationDialogState('numbers');
			setConfirmationDialog(true);
		} else {
			setNumbersToAdd(selectedNumbers);
		}
	};

	const cancelSkipClicked = () => {
		setConfirmationDialog(false);
		if (confirmationDialogState === 'departments') {
			setIsDialogOpen(true);
		} else if (confirmationDialogState === 'numbers') {
			setNumbersToAdd(selectedNumbers);
		}
	};

	const changeAddClicked = () => {
		if (confirmationDialogState === 'departments') {
			if (filteredNumbers.length !== numbersWithUserAssigned.length) {
				setConfirmationDialogState('numbers');
			} else {
				setConfirmationDialog(false);
				setNumbersToAdd(selectedNumbers);
			}
		} else if (confirmationDialogState === 'numbers') {
			setConfirmationDialog(false);
			const accountNums: Array<string> = _.map(selectedNumbers, 'accountNum');
			const numbersToAdd = _.filter(
				filteredNumbers,
				(number: SingleNumber) => accountNums.indexOf(number.accountNum) < 0
			);
			setNumbersToAdd(() => [...selectedNumbers, ...numbersToAdd]);
		}
	};

	useEffect(() => {
		if (numbersToAdd.length > 0) {
			dispatch(showLoadingIndicator());
			const accountNums = _.map(numbersToAdd, 'accountNum');
			DepartmentsService.assignNumbersToDepartment({
				numbers: accountNums,
				departmentId: selectedDepartmentId,
			})
				.pipe(finalize(() => dispatch(hideLoadingIndicator())))
				.subscribe(({ response }) => {
					if (response.arguments.returnValue) {
						clearDialog();
						invalidateCache(
							[
								'getNumbersForAccount',
								'getDepartmentsForCurrentUser',
								'getDepartmentDetails',
								'getCorpUserNumbersWithChilds',
							],
							true
						);
						fetchNumbers();
						dispatch(
							setToast({
								error: false,
								label: translate('departments.numbers.added'),
								actionType: translate('added'),
							})
						);
						numbersAdded();
					}
				});
		}
		// eslint-disable-next-line
	}, [numbersToAdd, dispatch]);

	return (
		<>
			<CustomDialog
				isOpen={isDialogOpen}
				hideDialog={() => {
					setIsDialogOpen(false);
					clearDialog();
				}}
				header={translate('departments.add.users.numbers')}
				className={classes.dialog}
				rightSideButtons={[
					{
						text: translate('cancel'),
						onClick: () => {
							setIsDialogOpen(false);
							clearDialog();
						},
						color: 'tertiary-light',
					},
					{
						text: `${translate('add')}${selectedNumbersLength}`,
						onClick: addNumbers,
						color: 'primary-light',
						disabled: selectedNumbers.length <= 0,
					},
				]}
				dialogBody={
					<DialogBody
						numbers={numbers}
						searchInput={searchInput}
						setSearchInput={(value) => setSearchInput(value)}
						notAssignedToDepartment={notAssignedToDepartment}
						setNotAssignedToDepartment={(value) => setNotAssignedToDepartment(value)}
						setSelectedNumbers={(value) => setSelectedNumbers(value)}
						loading={loading}
					/>
				}
			/>
			<AddNumbersConfirmationDialog
				departmentsToChange={departmentsToChange}
				confirmationDialogState={confirmationDialogState}
				filteredNumbers={filteredNumbers}
				selectedNumbers={numbersWithUserAssigned}
				cancelSkipClicked={cancelSkipClicked}
				changeAddClicked={changeAddClicked}
				isOpen={confirmationDialog}
				hideDialog={() => setConfirmationDialog(false)}
			/>
		</>
	);
};

export default AddNumbersDialog;
